import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore, useIsAdminAuthenticated, useIsAuthenticated } from '@/stores/auth-store';
import { ROUTES } from '@/lib/constants';
import { routeForUser } from '@/features/auth/hooks';
import { PageSpinner } from '@/components/ui/spinner';

/** Sends unauthenticated visitors to /login while preserving the intended target. */
export function ProtectedRoute() {
  const authed = useIsAuthenticated();
  const hydrated = useAuthStore((s) => s.hydrated);
  const location = useLocation();

  if (!hydrated) return <PageSpinner />;
  if (!authed) {
    return <Navigate to={ROUTES.login} replace state={{ from: location.pathname }} />;
  }
  return <Outlet />;
}

/** Public-only routes (login/signup) that bounce already-authed users to their home. */
export function PublicOnlyRoute() {
  const authed = useIsAuthenticated();
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);

  if (!hydrated) return <PageSpinner />;
  if (authed) {
    // Honour role *and* onboarding state: a freshly-signed-up employer goes to
    // employer onboarding, a new candidate to profile completion, completed
    // users to their home. Mirrors the post-login routing in routeForUser().
    return <Navigate to={user ? routeForUser(user) : ROUTES.dashboard} replace />;
  }
  return <Outlet />;
}

/** Restricts a route to a single user role. */
export function RoleRoute({ role }: { role: 'candidate' | 'employer' }) {
  const user = useAuthStore((s) => s.user);
  const hydrated = useAuthStore((s) => s.hydrated);
  if (!hydrated) return <PageSpinner />;
  if (!user) return <Navigate to={ROUTES.login} replace />;
  const userRole = user.role === 'job_seeker' ? 'candidate' : user.role;
  if (userRole !== role) {
    return <Navigate to={ROUTES.dashboard} replace />;
  }
  return <Outlet />;
}

export function AdminProtectedRoute() {
  const authed = useIsAdminAuthenticated();
  const hydrated = useAuthStore((s) => s.hydrated);
  if (!hydrated) return <PageSpinner />;
  if (!authed) return <Navigate to={ROUTES.adminLogin} replace />;
  return <Outlet />;
}
